/**
 * A time zone is valid when the runtime can build a formatter for it — `Intl.DateTimeFormat`
 * throws `RangeError` on an unknown zone. This accepts everything the platform accepts (`UTC`,
 * `Etc/GMT+5`, backward-compatible aliases) where `Intl.supportedValuesOf('timeZone')` lists only
 * canonical names and omits `UTC` entirely, which blocked the form on some runtimes.
 */
export function isValidTimezone(value: string): boolean {
  const trimmed = value.trim();
  if (trimmed.length === 0) return false;
  try {
    new Intl.DateTimeFormat('en', { timeZone: trimmed });
    return true;
  } catch {
    return false;
  }
}

/** The runtime's resolved zone, falling back to `UTC` when the runtime reports none. */
export function defaultTimezone(): string {
  const resolved = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return typeof resolved === 'string' && resolved.length > 0 ? resolved : 'UTC';
}

/** Shown when the runtime cannot list its zones (`Intl.supportedValuesOf` missing). */
const FALLBACK_ZONES: readonly string[] = [
  'UTC',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Mexico_City',
  'America/Costa_Rica',
  'America/Toronto',
  'America/Sao_Paulo',
  'Europe/London',
  'Europe/Paris',
  'Europe/Madrid',
  'Europe/Lisbon',
  'Europe/Zurich',
  'Asia/Tokyo',
  'Australia/Sydney',
  'Pacific/Auckland',
];

/** The region an IANA zone is grouped under (`America/Costa_Rica` → `America`; `UTC` → `UTC`). */
export function timezoneRegion(zone: string): string {
  const slash = zone.indexOf('/');
  return slash < 0 ? zone : zone.slice(0, slash);
}

/**
 * Every zone the time-zone picker offers: the runtime's canonical list (or a short static list
 * when it cannot enumerate), plus `include` (the browser's and the current value — `UTC` and
 * aliases are valid but unlisted), sorted by region then name so the picker can group them.
 */
export function timezoneOptions(include: readonly string[] = []): string[] {
  let listed: readonly string[];
  try {
    listed =
      typeof Intl.supportedValuesOf === 'function'
        ? Intl.supportedValuesOf('timeZone')
        : FALLBACK_ZONES;
  } catch {
    listed = FALLBACK_ZONES;
  }
  const all = new Set<string>(listed.length > 0 ? listed : FALLBACK_ZONES);
  for (const zone of include) if (isValidTimezone(zone)) all.add(zone.trim());
  return [...all].sort((a, b) => {
    const region = timezoneRegion(a).localeCompare(timezoneRegion(b));
    return region !== 0 ? region : a.localeCompare(b);
  });
}
